/**
 * App Content Component
 * Main UI content - separated from App to be wrapped by providers
 */

import { useState, useCallback, lazy, Suspense } from 'react';
import { EmptyState } from './components/EmptyState/EmptyState';
import { AppLayout, ToastContainer } from '@midi-volca-drum/ui-components';
import { Header } from './components/Layout/Header';
import { ParameterDisplay } from './components/ParameterDisplay/ParameterDisplay';
import { useMIDIStore } from '@midi-volca-drum/midi';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';

// Lazy load modals for better initial bundle size
const AboutModal = lazy(() =>
  import('./components/Modals/AboutModal').then(module => ({ default: module.AboutModal }))
);
const MidiConnectModal = lazy(() =>
  import('./components/Modals/MidiConnectModal').then(module => ({
    default: module.MidiConnectModal,
  }))
);

// Lazy load main editor components (only loaded when MIDI is connected)
const PartTabs = lazy(() =>
  import('./components/Layout/PartTabs').then(module => ({ default: module.PartTabs }))
);
const PartCard = lazy(() =>
  import('./components/Layout/PartCard').then(module => ({ default: module.PartCard }))
);

export const AppContent = () => {
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);

  const connectionState = useMIDIStore(state => state.connectionState);

  // DEV mode: Bypass MIDI connection requirement for UI development
  const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';
  const showUI = isDevMode || connectionState.isConnected;

  // Close modals with Escape key
  useKeyboardShortcuts({
    onEscape: useCallback(() => {
      setShowAboutModal(false);
      setShowConnectModal(false);
    }, []),
    enablePartSwitching: true,
  });

  const handleOpenConnectModal = useCallback(() => {
    setShowConnectModal(true);
  }, []);

  const handleOpenAboutModal = useCallback(() => {
    setShowAboutModal(true);
  }, []);

  return (
    <AppLayout>
      <Header
        connected={connectionState.isConnected}
        onConnectClick={handleOpenConnectModal}
        onAboutClick={handleOpenAboutModal}
      />

      {showUI ? (
        <Suspense fallback={<div style={{ flex: 1 }} />}>
          <PartTabs />
          <PartCard />
        </Suspense>
      ) : (
        <EmptyState onConnectClick={handleOpenConnectModal} />
      )}

      {/* Parameter Display */}
      <ParameterDisplay />

      {/* Modals - Lazy loaded */}
      <Suspense fallback={null}>
        {showConnectModal && (
          <MidiConnectModal isOpen={showConnectModal} onClose={() => setShowConnectModal(false)} />
        )}
        {showAboutModal && (
          <AboutModal isOpen={showAboutModal} onClose={() => setShowAboutModal(false)} />
        )}
      </Suspense>

      {/* Toast Notifications */}
      <ToastContainer />
    </AppLayout>
  );
};
